import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const AssessmentModal = ({ assessment, onClose }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [basicInfo, setBasicInfo] = useState({});
  const [symptoms, setSymptoms] = useState({});
  const [impact, setImpact] = useState({});
  const [history, setHistory] = useState({});
  const [consent, setConsent] = useState({});

  const steps = ["Basic Info", "Symptoms", "Impact", "History", "Consent"];

  useEffect(() => {
    if (assessment && assessment.responses) {
      setBasicInfo(assessment.responses.basicInfo || {});
      setSymptoms(assessment.responses.symptoms || {});
      setImpact(assessment.responses.impact || {});
      setHistory(assessment.responses.History || {});
      setConsent(assessment.responses.consent || {});
    }
  }, [assessment]);

  const nextStep = () => {
    setCurrentStep((prev) => prev + 1);
  };

  const prevStep = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <BasicInfoForm data={basicInfo} isReadOnly={true} />;
      case 2:
        return <SymptomsStep data={symptoms} isReadOnly={true} />;
      case 3:
        return <ImpactStep data={impact} isReadOnly={true} />;
      case 4:
        return <HistoryStep data={history} isReadOnly={true} />;
      case 5:
        return <ConsentStep data={consent} isReadOnly={true} />;
      default:
        return <div>Unknown Step</div>;
    }
  };

  if (!assessment) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ">
      <div className="bg-white rounded-lg w-full max-w-xl mx-auto my-20 shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">
              Assessment Review - {assessment.patient?.name || 'Unknown Patient'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Status: {assessment.status} | Risk: {assessment.riskFlags?.[0] || 'Unknown'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              Step {currentStep} of {steps.length}
            </span>
            <span className="text-sm text-gray-500">
              {steps[currentStep - 1]}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / steps.length) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto modal-scrollbar" style={{ maxHeight: '60vh', minHeight: '200px' }}>
          <div className="mb-6">
            <p className="flex items-center text-sm text-gray-600 bg-blue-50 p-4 rounded-lg border-l-4 border-blue-600">
              <svg
                className="w-5 h-5 mr-3 text-blue-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              Reviewing patient assessment. This is a read-only view.
            </p>
          </div>

          {renderStepContent()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={prevStep}
            disabled={currentStep === 1}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M15 19l-7-7 7-7"
              ></path>
            </svg>
            Previous
          </button>

          <div className="flex items-center space-x-2">
            {steps.map((step, index) => (
              <button
                key={index}
                onClick={() => setCurrentStep(index + 1)}
                className={`w-2 h-2 rounded-full ${
                  index + 1 === currentStep
                    ? 'bg-blue-600'
                    : index + 1 < currentStep
                    ? 'bg-blue-300'
                    : 'bg-gray-300'
                }`}
              />
            ))}
          </div>

          <button
            onClick={nextStep}
            disabled={currentStep === steps.length}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 flex items-center"
          >
            Next
            <svg
              className="w-4 h-4 ml-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 5l7 7-7 7"
              ></path>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

// Basic Info Form Component
const BasicInfoForm = ({ data, isReadOnly }) => (
  <div className="space-y-6">
    <h3 className="text-lg font-semibold text-gray-800">Basic Information</h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Age</label>
        <p className="text-gray-900">{data.age || 'Not provided'}</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
        <p className="text-gray-900">{data.gender || 'Not provided'}</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
        <p className="text-gray-900">{data.language || 'Not provided'}</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
        <p className="text-gray-900">{data.timezone || 'Not provided'}</p>
      </div>
    </div>
  </div>
);

// Symptoms Step Component
const SymptomsStep = ({ data, isReadOnly }) => (
  <div className="space-y-6 ">
    <h3 className="text-lg font-semibold text-gray-800">ADHD Symptoms</h3>
    <div className="space-y-4">
      {Object.entries(data).map(([key, value]) => (
        <div key={key}>
          <label className="block text-sm font-medium text-gray-700 mb-2 capitalize">
            {key.replace(/([A-Z])/g, ' $1').trim()}
          </label>
          <p className="text-gray-900">{value || 'Not provided'}</p>
        </div>
      ))}
    </div>
  </div>
);

// Impact Step Component
const ImpactStep = ({ data, isReadOnly }) => (
  <div className="space-y-6">
    <h3 className="text-lg font-semibold text-gray-800">Impact Assessment</h3>
    <div className="space-y-4">
      {Object.entries(data).map(([key, value]) => (
        <div key={key}>
          <label className="block text-sm font-medium text-gray-700 mb-2 capitalize">
            {key.replace(/([A-Z])/g, ' $1').trim()}
          </label>
          {typeof value === 'number' ? (
            <div className="flex items-center">
              <div className="w-full bg-gray-200 rounded-full h-2 mr-4">
                <div
                  className="bg-blue-500 h-2 rounded-full"
                  style={{ width: `${value}%` }}
                ></div>
              </div>
              <span className="text-sm text-gray-600">{value}%</span>
            </div>
          ) : (
            <p className="text-gray-900">{value || 'Not provided'}</p>
          )}
        </div>
      ))}
    </div>
  </div>
);

// History Step Component
const HistoryStep = ({ data, isReadOnly }) => (
  <div className="space-y-6">
    <h3 className="text-lg font-semibold text-gray-800">Medical History</h3>
    <div className="space-y-4">
      {Object.entries(data).map(([key, value]) => (
        <div key={key}>
          <label className="block text-sm font-medium text-gray-700 mb-2 capitalize">
            {key.replace(/([A-Z])/g, ' $1').trim()}
          </label>
          <p className="text-gray-900">{value || 'Not provided'}</p>
        </div>
      ))}
    </div>
  </div>
);

// Consent Step Component
const ConsentStep = ({ data, isReadOnly }) => (
  <div className="space-y-6">
    <h3 className="text-lg font-semibold text-gray-800">Consent & Agreements</h3>
    <div className="space-y-4">
      {Object.entries(data).map(([key, value]) => (
        <div key={key} className="flex items-center">
          <div className={`w-4 h-4 rounded border-2 mr-3 flex items-center justify-center ${
            value ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
          }`}>
            {value && (
              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            )}
          </div>
          <label className="text-sm font-medium text-gray-700 capitalize">
            {key.replace(/([A-Z])/g, ' $1').trim()}
          </label>
        </div>
      ))}
    </div>
  </div>
);

export default AssessmentModal; 